"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import summaryApi from "../common/index";

export default function PaymentSuccess() {
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [orderData, setOrderData] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const hasProcessedRef = useRef(false); // Use ref to prevent duplicate calls without triggering re-renders

  useEffect(() => {
    // Prevent multiple calls - only process once
    if (hasProcessedRef.current || !sessionId) {
      if (!sessionId) {
        setStatus("error");
        toast.error("No session ID found");
      }
      return;
    }

    const verifyPaymentAndCreateOrder = async () => {
      // Set flag immediately to prevent duplicate calls
      hasProcessedRef.current = true;

      try {
        const res = await fetch(summaryApi.verifyPaymentAndCreateOrder.url, {
          method: summaryApi.verifyPaymentAndCreateOrder.method,
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId }),
        });

        const data = await res.json();

        if (data.success) {
          setStatus("success");
          setOrderData(data.data);
          toast.custom((t) => (
            <div
              className={`${
                t.visible ? "animate-enter" : "animate-leave"
              } max-w-md w-full bg-black border-l-4 border-yellow-500 shadow-lg rounded-md pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            >
              <div className="flex-1 w-0 p-4">
                <p className="text-sm font-bold text-yellow-400">
                  Payment Successful!
                </p>
                <p className="mt-1 text-sm text-white">
                  Your order has been placed successfully.
                </p>
              </div>
            </div>
          ));
        } else {
          setStatus("error");
          toast.error(data.message || "Failed to create order after payment");
        }
      } catch (err) {
        console.error("Payment verification error:", err);
        setStatus("error");
        toast.error("An error occurred while processing your order");
        // Reset flag on error so user can retry if needed
        hasProcessedRef.current = false;
      }
    };

    verifyPaymentAndCreateOrder();
  }, [sessionId]); // Only depend on sessionId - ref doesn't need to be in dependencies

  const handleContinueShopping = () => {
    navigate("/products");
  };

  const handleViewOrders = async () => {
    // Try to get user ID from order data, otherwise fetch current user
    if (orderData?.userId) {
      navigate("/user-profile/" + orderData.userId);
    } else {
      // Fallback: fetch current user details
      try {
        const res = await fetch(summaryApi.current_user.url, {
          method: summaryApi.current_user.method,
          credentials: "include",
        });
        const data = await res.json();
        if (data.success && data.data?._id) {
          navigate("/user-profile/" + data.data._id);
        } else {
          navigate("/products");
        }
      } catch (err) {
        navigate("/products");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {status === "processing" && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Processing Payment...
            </h2>
            <p className="text-gray-600">
              Please wait while we verify your payment and create your order.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Successful!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for your purchase. Your order has been placed successfully.
            </p>
            {orderData && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-semibold">Order ID:</span>{" "}
                  {orderData._id}
                </p>
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-semibold">Total Amount:</span> £
                  {orderData.totalAmount?.toLocaleString("en-IN")}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">Status:</span>{" "}
                  {orderData.status}
                </p>
              </div>
            )}
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleContinueShopping}
                className="px-6 py-2 bg-gray-900 text-white rounded hover:bg-black transition"
              >
                Continue Shopping
              </button>
              <button
                onClick={handleViewOrders}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
              >
                View Orders
              </button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Failed
            </h2>
            <p className="text-gray-600 mb-6">
              There was an error processing your payment. Please try again or
              contact support if the problem persists.
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate("/checkout")}
                className="px-6 py-2 bg-gray-900 text-white rounded hover:bg-black transition"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
              >
                Go Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

